import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useWallet } from '../hooks/useWallet';
import './Dashboard.css';

const REST = 'https://rest.amitis.network';
const RPC = 'https://rpc.amitis.network';

export default function Dashboard() {
  const { wallet, balance } = useWallet();
  const [chainStats, setChainStats] = useState(null);
  const [txHistory, setTxHistory] = useState([]);

  useEffect(() => {
    fetchChainStats();
    const interval = setInterval(fetchChainStats, 10000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (wallet?.address) fetchTxHistory(wallet.address);
  }, [wallet]);

  const fetchChainStats = async () => {
    try {
      const [statusRes, validatorsRes] = await Promise.all([
        fetch(`${RPC}/status`),
        fetch(`${REST}/cosmos/staking/v1beta1/validators?status=BOND_STATUS_BONDED`)
      ]);
      const status = await statusRes.json();
      const validators = await validatorsRes.json();
      setChainStats({
        height: status.result?.sync_info?.latest_block_height,
        validators: validators.validators?.length || 0,
        time: status.result?.sync_info?.latest_block_time,
      });
    } catch (e) {}
  };

  const fetchTxHistory = async (address) => {
    try {
      const res = await fetch(`${REST}/cosmos/tx/v1beta1/txs?events=message.sender='${address}'&limit=5`);
      const data = await res.json();
      setTxHistory(data.txs || []);
    } catch (e) {}
  };

  const formatTime = (isoString) => {
    if (!isoString) return '—';
    return new Date(isoString).toLocaleTimeString();
  };

  const formatHeight = (h) => h ? parseInt(h).toLocaleString() : '—';

  return (
    <div className="page dashboard">
      <div className="page-header">
        <h1 className="page-title">
          {wallet ? (
            <>Welcome back, <span className="italic green">friend.</span></>
          ) : (
            <>Amitis <span className="italic green">Network.</span></>
          )}
        </h1>
        <p className="page-sub">
          {wallet
            ? 'Every transaction returns 80% of fees directly to you.'
            : 'Connect your wallet to start experiencing cooperative blockchain.'}
        </p>
      </div>

      {/* Stats Bar */}
      <div className="stats-grid">
        <div className="stat-card">
          <span className="stat-label mono">Fee Rebate</span>
          <span className="stat-value green">80%</span>
          <span className="stat-note">returned to you</span>
        </div>
        <div className="stat-card">
          <span className="stat-label mono">Block Height</span>
          <span className="stat-value">{formatHeight(chainStats?.height)}</span>
          <span className="stat-note">latest block</span>
        </div>
        <div className="stat-card">
          <span className="stat-label mono">Validators</span>
          <span className="stat-value">{chainStats?.validators || '—'}</span>
          <span className="stat-note">of 21 petals active</span>
        </div>
        <div className="stat-card">
          <span className="stat-label mono">Your Balance</span>
          <span className="stat-value green">
            {wallet ? (balance !== null ? balance.toLocaleString(undefined, { maximumFractionDigits: 2 }) : '...') : '—'}
          </span>
          <span className="stat-note">AMTS</span>
        </div>
      </div>

      {/* Action Cards */}
      <div className="action-grid">
        <Link to="/faucet" className="action-card faucet-card">
          <div className="action-icon">💧</div>
          <div className="action-content">
            <h3>Claim Free AMTS</h3>
            <p>Get 100 AMTS to start using the network. Experience the 80% fee rebate firsthand.</p>
          </div>
          <span className="action-arrow">→</span>
        </Link>

        <Link to="/swap" className="action-card">
          <div className="action-icon">⚡</div>
          <div className="action-content">
            <h3>Swap Tokens</h3>
            <p>Trade on the native DEX with the lowest effective fees in Cosmos.</p>
          </div>
          <span className="action-arrow">→</span>
        </Link>

        <Link to="/bridge" className="action-card">
          <div className="action-icon">🌐</div>
          <div className="action-content">
            <h3>Bridge Assets</h3>
            <p>Move OSMO, ATOM, and other Cosmos tokens to Amitis via IBC.</p>
          </div>
          <span className="action-arrow">→</span>
        </Link>
      </div>

      {/* Rebate Explainer */}
      <div className="rebate-banner">
        <div className="rebate-number green">80<span style={{fontSize:'0.5em', verticalAlign:'top', marginTop:'0.2em', display:'inline-block'}}>%</span></div>
        <div className="rebate-content">
          <h2>Every transaction. Every user. Every time.</h2>
          <p>80% of every fee you pay is automatically returned to your wallet. Not a promotion. Not a smart contract. Hardcoded into the protocol.</p>
          <div className="rebate-math mono">
            <span>0.3% fee charged</span>
            <span className="green">→ 0.24% returned instantly</span>
            <span>= 0.06% effective cost</span>
          </div>
        </div>
      </div>

      {/* TX History */}
      {wallet && txHistory.length > 0 && (
        <div className="tx-history">
          <h3 className="section-title">Recent Transactions</h3>
          <div className="tx-list">
            {txHistory.slice(0, 5).map((tx, i) => (
              <div key={i} className="tx-item">
                <span className="tx-hash mono">{tx.txhash?.slice(0, 16)}...</span>
                <span className="tx-height">Block {tx.height}</span>
                <a
                  href={`https://amitis.network/explorer?tx=${tx.txhash}`}
                  target="_blank"
                  rel="noreferrer"
                  className="tx-link"
                >↗</a>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
